import React, { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const formatPrice = (price) => {
  if (price === undefined || price === null || price === '') return '';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

const getProductImageUrl = (product) => {
  if (product?.photos?.length) {
    return product.photos[0];
  }
  if (product?.photo) {
    return product.photo;
  }
  return 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80';
};

const CatalogPreviewModal = ({ products = [], onClose }) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const dateLabel = new Date().toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-secondary-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col border border-slate-200"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-primary-700 via-primary-500 to-primary-400" />
        <div className="flex items-center justify-between px-5 sm:px-8 py-5 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-4 min-w-0">
            <img src="/Aalap.png" alt="Aalap Electronics" className="h-9 w-auto object-contain" />
            <div className="min-w-0 border-l border-slate-200 pl-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-600 mb-0.5">
                Catalog Preview
              </p>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900">Product Catalog</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {products.length} selected • {dateLabel}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-200 transition-colors"
            aria-label="Close catalog preview"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 sm:p-8 bg-slate-50">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((product) => (
              <article
                key={product.id}
                className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm"
              >
                <div className="aspect-[4/3] bg-slate-50 border-b border-slate-100 flex items-center justify-center overflow-hidden">
                  <img
                    src={getProductImageUrl(product)}
                    alt={product.name}
                    className="max-w-full max-h-full object-contain p-3"
                    onError={(event) => {
                      event.target.src = 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80';
                    }}
                  />
                </div>
                <div className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-600 mb-1">
                    {[product.brandName || product.brand, product.categoryName || product.category]
                      .filter(Boolean)
                      .join(' • ')}
                  </p>
                  <h3 className="font-semibold text-slate-900 line-clamp-2 text-sm leading-snug">
                    {product.name}
                  </h3>
                  <div className="flex items-end justify-between mt-3 pt-3 border-t border-slate-100 gap-2">
                    {product.mrp ? (
                      <span className="text-xs text-slate-500">
                        MRP: <span className="line-through">{formatPrice(product.mrp)}</span>
                      </span>
                    ) : <span />}
                    {product.offerPrice ? (
                      <span className="text-sm font-bold text-emerald-600">
                        {formatPrice(product.offerPrice)}
                      </span>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="px-5 sm:px-8 py-3 border-t border-slate-200 text-xs text-slate-500 bg-white">
          Aalap Electronics • Swastik Cross Roads Corner, Ahmedabad 380009 • +91 96388 89898
        </div>
      </div>
    </div>
  );
};

export default CatalogPreviewModal;
